/**
 * JournalGeneratorPage - Dedicated page for creating and managing AI-generated journals
 * Route: /admin/generator
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import AppLayout from "@/components/layout/AppLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminRole } from "@/hooks/useAdminRole";
import { useJournals, useCreateJournal, useDeleteJournal, useUpdateJournal } from "@/hooks/useJournals";
import { useGenerateJournalContent, GenerateJournalOptions } from "@/hooks/useGenerateJournal";
import { useShareJournal } from "@/hooks/useShareJournal";
import { Journal, JournalAIContext } from "@/types/journal";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    TooltipProvider,
} from "@/components/ui/tooltip";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
    ChevronDown,
    ChevronLeft,
    Loader2,
    Trash2,
    ExternalLink,
    Sparkles,
    BookOpen,
    AlertCircle,
    Globe,
    MoreHorizontal,
    FileText,
} from "lucide-react";
import { format } from "date-fns";

export default function JournalGeneratorPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { isAdmin, isLoading: roleLoading } = useAdminRole();
    const { data: journals, isLoading: journalsLoading } = useJournals();
    const createMutation = useCreateJournal();
    const generateMutation = useGenerateJournalContent();
    const deleteMutation = useDeleteJournal();
    const updateMutation = useUpdateJournal();
    const shareMutation = useShareJournal();

    // Query block counts for all journals
    const { data: blockCounts } = useQuery({
        queryKey: ['journal-block-counts'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('journal_blocks')
                .select('journal_id');
            if (error) throw error;
            
            const counts: Record<string, number> = {};
            data?.forEach(block => {
                counts[block.journal_id] = (counts[block.journal_id] || 0) + 1;
            });
            return counts;
        },
    });

    const getBlockCount = (journalId: string) => blockCounts?.[journalId] || 0;

    // Form state
    const [topicPrompt, setTopicPrompt] = useState("");
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [options, setOptions] = useState<GenerateJournalOptions>({
        max_modules: 3,
        generate_youtube_videos: true,
        generate_quizzes: true,
        quiz_difficulty: "hard",
        generate_todos: true,
        share_to_community: false,
    });

    // Track which journals are currently generating
    const [generatingIds, setGeneratingIds] = useState<Set<string>>(new Set());

    // Prompt edit modal state
    const [editingJournal, setEditingJournal] = useState<Journal | null>(null);
    const [editedPrompt, setEditedPrompt] = useState("");
    const [isViewOnly, setIsViewOnly] = useState(false);

    // Redirect non-admins
    useEffect(() => {
        if (!roleLoading && !isAdmin) {
            navigate("/");
        }
    }, [isAdmin, roleLoading, navigate]);

    // Extract title from prompt (first line or first N chars)
    const extractTitle = (prompt: string): string => {
        const match = prompt.match(/for\s+([^.]+)/i);
        if (match) return match[1].trim().slice(0, 50);
        return prompt.slice(0, 50) + (prompt.length > 50 ? "..." : "");
    };

    // Step 1: Create draft journal with ai_context
    const handleCreateDraft = async () => {
        if (!topicPrompt.trim() || topicPrompt.length < 20) return;

        const title = extractTitle(topicPrompt);
        await createMutation.mutateAsync({
            name: title,
            ai_context: {
                generation_status: 'draft',
                original_prompt: topicPrompt,
                generation_options: options as Record<string, unknown>,
            }
        });
        setTopicPrompt(""); // Clear form
    };

    // Helper to safely get ai_context
    const getAIContext = (journal: Journal): JournalAIContext | null => {
        if (!journal.ai_context) return null;
        return journal.ai_context as JournalAIContext;
    };

    // Step 2: Generate content for existing journal
    const handleGenerate = async (journal: Journal) => {
        const aiContext = getAIContext(journal);
        // Use stored prompt or fallback to journal name
        const prompt = aiContext?.original_prompt ||
            `Create a comprehensive learning-journal for ${journal.name}`;

        // Use stored options or current form options
        const genOptions = (aiContext?.generation_options as GenerateJournalOptions) || options;

        // Update status to generating
        setGeneratingIds((prev) => new Set(prev).add(journal.id));
        await updateMutation.mutateAsync({
            journalId: journal.id,
            updates: { ai_context: { ...aiContext, generation_status: 'generating' } }
        });

        try {
            await generateMutation.mutateAsync({
                journal_id: journal.id,
                topic_prompt: prompt,
                options: genOptions,
            });
            // Update status to ready on success
            await updateMutation.mutateAsync({
                journalId: journal.id,
                updates: { ai_context: { ...aiContext, generation_status: 'ready', generated_at: new Date().toISOString() } }
            });
        } catch (error) {
            // Revert to draft on error
            await updateMutation.mutateAsync({
                journalId: journal.id,
                updates: { ai_context: { ...aiContext, generation_status: 'draft' } }
            });
            throw error;
        } finally {
            setGeneratingIds((prev) => {
                const next = new Set(prev);
                next.delete(journal.id);
                return next;
            });
        }
    };

    // Determine journal status - simpler logic based on blocks and sharing
    const getStatus = (journal: Journal): "draft" | "generating" | "shared" | "ready" => {
        // Currently generating
        if (generatingIds.has(journal.id)) return "generating";
        
        // Shared to community
        if (journal.sharing_mode === "community") return "shared";
        
        // Has blocks = ready/generated
        if (getBlockCount(journal.id) > 0) return "ready";
        
        // No blocks = draft
        return "draft";
    };

    // Handle generate with edited prompt
    const handleGenerateWithPrompt = async (journal: Journal, prompt: string) => {
        setEditingJournal(null);
        
        const aiContext = getAIContext(journal);
        const genOptions = (aiContext?.generation_options as GenerateJournalOptions) || options;

        // Update status to generating
        setGeneratingIds((prev) => new Set(prev).add(journal.id));
        await updateMutation.mutateAsync({
            journalId: journal.id,
            updates: { ai_context: { ...aiContext, generation_status: 'generating', original_prompt: prompt } }
        });

        try {
            await generateMutation.mutateAsync({
                journal_id: journal.id,
                topic_prompt: prompt,
                options: genOptions,
            });
            await updateMutation.mutateAsync({
                journalId: journal.id,
                updates: { ai_context: { ...aiContext, generation_status: 'ready', original_prompt: prompt, generated_at: new Date().toISOString() } }
            });
        } catch (error) {
            await updateMutation.mutateAsync({
                journalId: journal.id,
                updates: { ai_context: { ...aiContext, generation_status: 'draft', original_prompt: prompt } }
            });
            throw error;
        } finally {
            setGeneratingIds((prev) => {
                const next = new Set(prev);
                next.delete(journal.id);
                return next;
            });
        }
    };

    // Handle share toggle
    const handleShareToggle = async (journal: Journal, share: boolean) => {
        await shareMutation.mutateAsync({
            journalId: journal.id,
            sharingMode: share ? 'community' : null,
            journalName: journal.name,
            journalSummary: journal.summary || '',
        });
    };

    // Loading state
    if (roleLoading) {
        return (
            <AppLayout pageTitle="Journal Generator">
                <div className="min-h-screen bg-background">
                    <div className="max-w-6xl mx-auto px-6 py-12">
                        <Skeleton className="h-10 w-64 mb-2" />
                        <Skeleton className="h-5 w-96 mb-12" />
                        <Skeleton className="h-64 rounded-2xl" />
                    </div>
                </div>
            </AppLayout>
        );
    }

    // Not admin (will redirect)
    if (!isAdmin) {
        return (
            <AppLayout pageTitle="Journal Generator">
                <div className="min-h-screen bg-background flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">Redirecting...</p>
                    </div>
                </div>
            </AppLayout>
        );
    }

    // Sort journals by created_at descending
    const sortedJournals = journals
        ? [...journals].sort(
            (a, b) =>
                new Date(b.created_at!).getTime() - new Date(a.created_at!).getTime()
        )
        : [];

    return (
        <AppLayout pageTitle="Journal Generator">
            <div className="h-full overflow-y-auto bg-background">
                <div className="max-w-6xl mx-auto px-6 py-12 space-y-8">
                    {/* Header */}
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate("/admin")}
                            className="gap-1"
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Admin
                        </Button>
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/10 rounded-xl">
                                <BookOpen className="w-5 h-5 text-primary" />
                            </div>
                            <h1 className="text-2xl font-bold text-foreground tracking-tight">
                                Journal Generator
                            </h1>
                        </div>
                    </div>

                    {/* Create Draft Form */}
                    <div className="bg-card rounded-2xl border border-border p-6 space-y-4 shadow-sm">
                        <h2 className="font-medium text-lg flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-primary" />
                            Create New Journal
                        </h2>

                        <div className="space-y-2">
                            <Label htmlFor="topic-prompt" className="text-sm font-medium">
                                Topic Prompt *
                            </Label>
                            <Textarea
                                id="topic-prompt"
                                placeholder="Create a comprehensive learning-journal outline for [Topic]. Include: (1) ..., (2) ..., (3) ..."
                                value={topicPrompt}
                                onChange={(e) => setTopicPrompt(e.target.value)}
                                disabled={createMutation.isPending}
                                className="min-h-[120px] resize-none"
                            />
                            <p className="text-xs text-muted-foreground">
                                Minimum 20 characters. Be specific about the topics and
                                structure you want.
                            </p>
                            {topicPrompt.length > 0 && topicPrompt.length < 20 && (
                                <p className="text-xs text-amber-600 flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    {20 - topicPrompt.length} more characters needed
                                </p>
                            )}
                        </div>

                        {/* Advanced Options */}
                        <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
                            <CollapsibleTrigger asChild>
                                <Button
                                    variant="ghost"
                                    className="w-full justify-between px-0 hover:bg-transparent"
                                >
                                    <span className="text-sm font-medium">Advanced Options</span>
                                    <ChevronDown
                                        className={`w-4 h-4 transition-transform ${showAdvanced ? "rotate-180" : ""
                                            }`}
                                    />
                                </Button>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="space-y-4 pt-4">
                                {/* Max Modules */}
                                <div className="space-y-2">
                                    <Label className="text-sm">Max Modules</Label>
                                    <Select
                                        value={String(options.max_modules || 5)}
                                        onValueChange={(v) =>
                                            setOptions({ ...options, max_modules: parseInt(v) })
                                        }
                                        disabled={createMutation.isPending}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {[1, 2, 3].map((n) => (
                                                <SelectItem key={n} value={String(n)}>
                                                    {n} modules
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Quiz Difficulty */}
                                <div className="space-y-2">
                                    <Label className="text-sm">Quiz Difficulty</Label>
                                    <Select
                                        value={options.quiz_difficulty || "hard"}
                                        onValueChange={(v) =>
                                            setOptions({
                                                ...options,
                                                quiz_difficulty: v as "easy" | "medium" | "hard",
                                            })
                                        }
                                        disabled={createMutation.isPending}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="easy">Easy</SelectItem>
                                            <SelectItem value="medium">Medium</SelectItem>
                                            <SelectItem value="hard">Hard</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Toggles */}
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="youtube" className="text-sm">
                                            Generate YouTube Videos
                                        </Label>
                                        <Switch
                                            id="youtube"
                                            checked={options.generate_youtube_videos}
                                            onCheckedChange={(v) =>
                                                setOptions({ ...options, generate_youtube_videos: v })
                                            }
                                            disabled={createMutation.isPending}
                                        />
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="quizzes" className="text-sm">
                                            Generate Quizzes
                                        </Label>
                                        <Switch
                                            id="quizzes"
                                            checked={options.generate_quizzes}
                                            onCheckedChange={(v) =>
                                                setOptions({ ...options, generate_quizzes: v })
                                            }
                                            disabled={createMutation.isPending}
                                        />
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="todos" className="text-sm">
                                            Generate To-do Items
                                        </Label>
                                        <Switch
                                            id="todos"
                                            checked={options.generate_todos}
                                            onCheckedChange={(v) =>
                                                setOptions({ ...options, generate_todos: v })
                                            }
                                            disabled={createMutation.isPending}
                                        />
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="share" className="text-sm">
                                            Share to Community
                                        </Label>
                                        <Switch
                                            id="share"
                                            checked={options.share_to_community}
                                            onCheckedChange={(v) =>
                                                setOptions({ ...options, share_to_community: v })
                                            }
                                            disabled={createMutation.isPending}
                                        />
                                    </div>
                                </div>
                            </CollapsibleContent>
                        </Collapsible>

                        <div className="flex justify-end pt-2">
                            <Button
                                onClick={handleCreateDraft}
                                disabled={topicPrompt.length < 20 || createMutation.isPending}
                            >
                                {createMutation.isPending ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="w-4 h-4 mr-2" />
                                        Create Draft
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Journals Table */}
                    <div className="bg-card rounded-2xl border border-border overflow-hidden shadow-sm">
                        <div className="px-6 py-4 border-b border-border">
                            <h2 className="font-semibold text-lg text-foreground">
                                Your Journals
                            </h2>
                        </div>
                        {journalsLoading ? (
                            <div className="p-8 text-center">
                                <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
                                <p className="text-muted-foreground mt-2">Loading journals...</p>
                            </div>
                        ) : sortedJournals.length === 0 ? (
                            <div className="p-8 text-center">
                                <div className="text-muted-foreground mb-2">
                                    <BookOpen className="w-12 h-12 mx-auto opacity-50" />
                                </div>
                                <p className="text-muted-foreground">
                                    No journals yet. Use the form above to create one!
                                </p>
                            </div>
                        ) : (
                            <TooltipProvider>
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/50">
                                            <TableHead className="font-semibold">Journal</TableHead>
                                            <TableHead className="font-semibold w-40">Prompt</TableHead>
                                            <TableHead className="font-semibold w-16 text-center">Blocks</TableHead>
                                            <TableHead className="font-semibold w-16 text-center">Views</TableHead>
                                            <TableHead className="font-semibold w-16 text-center">Copies</TableHead>
                                            <TableHead className="font-semibold w-24">Status</TableHead>
                                            <TableHead className="font-semibold w-12 text-right">
                                                <span className="sr-only">Actions</span>
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {sortedJournals.map((journal) => {
                                            const status = getStatus(journal);
                                            const blockCount = getBlockCount(journal.id);
                                            const aiContext = getAIContext(journal);
                                            const originalPrompt = aiContext?.original_prompt || "";
                                            
                                            const openPromptModal = (viewOnly: boolean) => {
                                                setEditingJournal(journal);
                                                setEditedPrompt(originalPrompt || `Create a comprehensive learning-journal for ${journal.name}`);
                                                setIsViewOnly(viewOnly);
                                            };
                                            
                                            return (
                                                <TableRow key={journal.id}>
                                                    {/* Journal name with created date and view icon */}
                                                    <TableCell className="font-medium">
                                                        <div className="flex items-center gap-2">
                                                            <a 
                                                                href={`/journals/${journal.id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-muted-foreground hover:text-primary transition-colors shrink-0"
                                                            >
                                                                <ExternalLink className="w-4 h-4" />
                                                            </a>
                                                            <div className="min-w-0">
                                                                <div className="truncate max-w-[200px] text-foreground">{journal.name}</div>
                                                                <div className="text-xs text-muted-foreground">
                                                                    {format(new Date(journal.created_at!), "MMM d, yyyy")}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    {/* Prompt preview */}
                                                    <TableCell>
                                                        {originalPrompt ? (
                                                            <button
                                                                onClick={() => openPromptModal(status !== "draft")}
                                                                className="text-left text-xs text-muted-foreground hover:text-primary truncate max-w-[140px] block transition-colors"
                                                                title="Click to view prompt"
                                                            >
                                                                {originalPrompt.slice(0, 40)}...
                                                            </button>
                                                        ) : (
                                                            <span className="text-xs text-muted-foreground/50">--</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground text-center">
                                                        {blockCount || "--"}
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground text-center">
                                                        {journal.view_count || "--"}
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground text-center">
                                                        {journal.copy_count || "--"}
                                                    </TableCell>
                                                    <TableCell>
                                                        <span
                                                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                                                                status === "generating"
                                                                    ? "bg-blue-100/80 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                                                                    : status === "shared"
                                                                        ? "bg-emerald-100/80 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                                                        : status === "ready"
                                                                            ? "bg-amber-100/80 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                                                                            : "bg-muted text-muted-foreground"
                                                            }`}
                                                        >
                                                            {status === "generating" && (
                                                                <Loader2 className="w-3 h-3 animate-spin" />
                                                            )}
                                                            {status === "shared" && (
                                                                <Globe className="w-3 h-3" />
                                                            )}
                                                            {status.charAt(0).toUpperCase() + status.slice(1)}
                                                        </span>
                                                    </TableCell>
                                                    {/* Actions dropdown */}
                                                    <TableCell className="text-right">
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                                                    <MoreHorizontal className="w-4 h-4" />
                                                                    <span className="sr-only">Open menu</span>
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end" className="w-48">
                                                                {/* Draft actions */}
                                                                {status === "draft" && (
                                                                    <DropdownMenuItem 
                                                                        onClick={() => openPromptModal(false)}
                                                                        disabled={generatingIds.size > 0}
                                                                    >
                                                                        <Sparkles className="w-4 h-4 mr-2" />
                                                                        Generate
                                                                    </DropdownMenuItem>
                                                                )}
                                                                
                                                                {/* Ready actions */}
                                                                {status === "ready" && (
                                                                    <>
                                                                        <DropdownMenuItem disabled className="text-muted-foreground">
                                                                            <Sparkles className="w-4 h-4 mr-2 opacity-50" />
                                                                            Generate ({blockCount} blocks)
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuItem onClick={() => handleShareToggle(journal, true)}>
                                                                            <Globe className="w-4 h-4 mr-2" />
                                                                            Share to Community
                                                                        </DropdownMenuItem>
                                                                    </>
                                                                )}
                                                                
                                                                {/* Shared actions */}
                                                                {status === "shared" && (
                                                                    <DropdownMenuItem onClick={() => handleShareToggle(journal, false)}>
                                                                        <Globe className="w-4 h-4 mr-2" />
                                                                        Unshare
                                                                    </DropdownMenuItem>
                                                                )}
                                                                
                                                                {/* Prompt action */}
                                                                {originalPrompt && (
                                                                    <DropdownMenuItem onClick={() => openPromptModal(status !== "draft")}>
                                                                        <FileText className="w-4 h-4 mr-2" />
                                                                        {status === "draft" ? "Edit Prompt" : "View Prompt"}
                                                                    </DropdownMenuItem>
                                                                )}
                                                                
                                                                <DropdownMenuSeparator />
                                                                
                                                                {/* Delete action */}
                                                                <DropdownMenuItem 
                                                                    onClick={() => {
                                                                        if (confirm(`Are you sure you want to delete "${journal.name}"?`)) {
                                                                            deleteMutation.mutate(journal.id);
                                                                        }
                                                                    }}
                                                                    className="text-destructive focus:text-destructive"
                                                                >
                                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                                    Delete
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TooltipProvider>
                        )}
                    </div>
                </div>
            </div>

            {/* Prompt Edit/View Modal */}
            <Dialog open={!!editingJournal} onOpenChange={(open) => { if (!open) { setEditingJournal(null); setIsViewOnly(false); } }}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{isViewOnly ? "View Generation Prompt" : "Edit Generation Prompt"}</DialogTitle>
                        <DialogDescription>
                            {isViewOnly 
                                ? `Original prompt used to generate "${editingJournal?.name}"`
                                : `Review and edit the prompt before generating content for "${editingJournal?.name}"`
                            }
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <Textarea
                            value={editedPrompt}
                            onChange={(e) => setEditedPrompt(e.target.value)}
                            className="min-h-[200px]"
                            placeholder="Enter generation prompt..."
                            readOnly={isViewOnly}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setEditingJournal(null); setIsViewOnly(false); }}>
                            {isViewOnly ? "Close" : "Cancel"}
                        </Button>
                        {!isViewOnly && (
                            <>
                                <Button 
                                    variant="secondary"
                                    onClick={async () => {
                                        if (editingJournal) {
                                            const aiContext = getAIContext(editingJournal);
                                            await updateMutation.mutateAsync({
                                                journalId: editingJournal.id,
                                                updates: { ai_context: { ...aiContext, original_prompt: editedPrompt } }
                                            });
                                            setEditingJournal(null);
                                        }
                                    }}
                                    disabled={updateMutation.isPending || !editedPrompt.trim()}
                                >
                                    Save
                                </Button>
                                <Button 
                                    onClick={() => editingJournal && handleGenerateWithPrompt(editingJournal, editedPrompt)}
                                    disabled={generatingIds.size > 0 || !editedPrompt.trim()}
                                >
                                    <Sparkles className="w-4 h-4 mr-2" />
                                    Generate
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppLayout>
    );
}
